/**
 * Tenant lifecycle types, served by the tenant-management backend under
 * `/api/tenant-management/orgs`.
 *
 * A ModernPath **staff admin who also holds instance-level IAM authority**
 * (`canManageTenants`) can provision and delete customer tenants (ZITADEL orgs)
 * from the console. The create payload mirrors the `tmctl create` CLI field-for-
 * field, so console-provisioned tenants are identical to CLI-provisioned ones.
 *
 * @module types/tenant
 */

/**
 * Body of `POST /api/tenant-management/orgs`. Required: orgName, orgDomain,
 * customerId, adminEmail, adminFirstName, adminLastName. subscriptionStatus
 * defaults to `trialing` server-side when empty; the numeric limits are optional
 * (0 = no limit / omit).
 */
export interface CreateTenantRequest {
  orgName: string;
  orgDomain: string;
  customerId: string;
  subscriptionStatus: string;
  entitlements: string[];
  seatLimit: number;
  projectLimit: number;
  adminEmail: string;
  adminFirstName: string;
  adminLastName: string;
}

/** Response of `POST /api/tenant-management/orgs`. */
export interface CreateTenantResponse {
  orgId: string;
  adminUserId: string;
  message: string;
}

/**
 * Response of `GET /api/tenant-management/orgs/{orgId}` — the tenant-details view.
 * `vendor`/`isDefault` mark the two protected system orgs (neither is deletable).
 */
export interface TenantDetails {
  id: string;
  name: string;
  primaryDomain: string;
  vendor: boolean;
  isDefault: boolean;
  userCount: number;
  customerId: string;
  subscriptionStatus: string;
  /** May be `null` when the org has no entitlements — the backend serialises an
   *  empty list as Go's nil slice (`null`), not `[]`. Normalise before use. */
  entitlements: string[] | null;
  seatLimit: number;
  projectLimit: number;
}

/** Response of `DELETE /api/tenant-management/orgs/{orgId}`. */
export interface DeleteTenantResponse {
  success: boolean;
  message: string;
}

/**
 * Body of `PATCH /api/tenant-management/orgs/{orgId}` — a partial tenant update.
 * Only the provided fields change; omitted fields are left untouched.
 *
 * `customerId` is deliberately absent: it is the immutable billing anchor set at
 * creation and the backend does not accept it. `orgDomain` is likewise not
 * editable (ZITADEL models domains as separate org resources).
 */
export interface UpdateTenantRequest {
  orgName?: string;
  subscriptionStatus?: string;
  entitlements?: string[];
  seatLimit?: number;
  projectLimit?: number;
}

/** Response of `PATCH /api/tenant-management/orgs/{orgId}`. */
export interface UpdateTenantResponse {
  success: boolean;
  message: string;
}

/** Subscription status values accepted by tenant creation (mirrors the CLI). */
export const SUBSCRIPTION_STATUSES = [
  'trialing',
  'active',
  'suspended',
  'expired',
  'cancelled',
] as const;

export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];
